import { Bot, Code2, FolderTree, History, LayoutPanelLeft, PanelBottom } from "lucide-react";
import type { IdeLayout } from "../lib/useIdeLayout.ts";
import { IconButton } from "./IconButton.tsx";
import { Menu } from "./Menu.tsx";

/**
 * Header controls for every IDE panel. One icon per panel (Files, Code, Tools, Agent, History), always labelled
 * (tooltip + aria-label) with an active state. The toggles are fully independent: Code and Tools can each be hidden,
 * or both at once. Hiding a panel only removes its slot: open files, unsaved edits, terminals and chats are kept.
 */
export function PanelControls({ layout }: { layout: IdeLayout }) {
  const { model } = layout;
  const historyOn = model.history.standalone;
  const other = (s: "left" | "right") => (s === "left" ? "right" : "left");
  return (
    <span className="panel-toggles" role="group" aria-label="Panels">
      <IconButton
        icon={FolderTree}
        label="Files (Ctrl/Cmd+B)"
        active={model.visible.files}
        onClick={() => layout.toggle("files")}
        testId="toggle-explorer"
      />
      <IconButton
        icon={Code2}
        label="Code editor"
        active={model.visible.code}
        onClick={() => layout.toggle("code")}
        testId="toggle-editor"
      />
      <IconButton
        icon={PanelBottom}
        label="Tools: terminal (Ctrl/Cmd+J)"
        active={model.visible.tools}
        onClick={() => layout.toggle("tools")}
        testId="toggle-bottom"
      />
      <IconButton
        icon={Bot}
        label="Agent"
        active={model.visible.agent}
        onClick={() => layout.toggle("agent")}
        testId="toggle-agent"
      />
      <IconButton
        icon={History}
        label={
          historyOn ? "History panel: click to return History to the Agent" : "History: open as its own panel"
        }
        active={historyOn}
        onClick={() => layout.setHistoryStandalone(!historyOn)}
        testId="toggle-history"
      />
      <Menu
        testId="layout-menu"
        trigger={<LayoutPanelLeft size={15} strokeWidth={1.75} aria-label="Layout" />}
        items={[
          {
            label: `Dock Files on the ${other(model.dock.files)}`,
            onSelect: () => layout.dock("files", other(model.dock.files)),
            testId: "dock-files",
          },
          {
            label: `Dock Agent on the ${other(model.dock.agent)}`,
            onSelect: () => layout.dock("agent", other(model.dock.agent)),
            testId: "dock-agent",
          },
          {
            label: `Place History on the ${other(model.history.side)}`,
            onSelect: () => layout.setHistorySide(other(model.history.side)),
            disabled: !historyOn,
            testId: "dock-history",
          },
          { label: "Reset layout", onSelect: layout.reset, testId: "layout-reset" },
        ]}
      />
    </span>
  );
}
